"use client";

import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useRouter } from "next/navigation";

import { signUpSchema } from "@/lib/zod";
import { OptionProps } from "@/types";
import { handleApiError } from "@/utils/form-error";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Form } from "react-bootstrap";
import { z } from "zod";

import AuthButton from "./auth-button";
import FloatingInput from "./floating-input";
import FloatingSelect from "./floating-select";
import FloatingSwitch from "./floating-switch";

import api, { ApiError } from "@/lib/api";

type FormFields = z.infer<typeof signUpSchema>;

type SignUpFormProps = {
  countryOptions: OptionProps[];
  affiliationOptions: OptionProps[];
};

export default function SignUpForm({ countryOptions, affiliationOptions }: SignUpFormProps) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(signUpSchema),
  });

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    if (!executeRecaptcha) {
      setError("root", {
        type: "manual",
        message: "reCAPTCHA not ready or failed to load.",
      });
      return;
    }

    try {
      const token = await executeRecaptcha("registration");

      await api.post("/users/", {
        ...data,
        recaptcha: token,
      });

      toast.success(
        "We’ve sent a confirmation email to your provided email address. To complete the sign-up process, please click the link in the email.",
      );
      toast.success(
        "If you don't see the email, check your spam or junk folder. Make sure the email address you entered during sign-up is correct.",
      );

      router.push("/");
    } catch (error) {
      if (error instanceof ApiError) {
        handleApiError(error, setError);
      } else {
        setError("root", {
          type: "manual",
          message: "An unexpected error occurred while validating the reCAPTCHA. Please try again later.",
        });
      }
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FloatingInput label="First Name" register={register("first_name")} error={errors.first_name} />
      <FloatingInput label="Last Name" register={register("last_name")} error={errors.last_name} />
      <FloatingSelect
        label="Country"
        name="country"
        options={countryOptions}
        register={register("country")}
        error={errors.country}
      />
      <FloatingSelect
        label="Affiliation"
        name="affiliation"
        options={affiliationOptions}
        register={register("affiliation")}
        error={errors.affiliation}
      />
      <FloatingInput type="email" label="Email Address" register={register("email")} error={errors.email} />
      <FloatingInput type="password" label="Password" register={register("password")} error={errors.password} />
      <FloatingInput
        type="password"
        label="Confirm Password"
        register={register("re_password")}
        error={errors.re_password}
        className="mb-4"
      />
      <FloatingSwitch
        label="Data alerts"
        name="data_alert"
        register={register("data_alert")}
        helpText="Receive notifications of dataset changes and upgrades."
      />
      <FloatingSwitch
        label="Announcements"
        name="announcement"
        register={register("announcement")}
        helpText="Receive announcements of newsletters, conferences and other items of interest to analysts."
      />
      {errors.root && <Alert variant="danger">{errors.root.message}</Alert>}
      <AuthButton title="Sign up" isLoading={isSubmitting} />
    </Form>
  );
}
